import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { useAsyncUploadBook } from '../hooks/useBooks';
import type { BookUploadDto } from '../types/book';
import { BookFormat } from '../types/book';
import type { PublisherDto } from '../types/publisher';
import { getActivePublishers } from '../api/publishers';
import { validateISBN } from '../utils/validation';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';

const MAX_FILE_SIZE = 3 * 1024 * 1024 * 1024; // 3GB
const ALLOWED_FORMATS = ['.epub', '.pdf'];

export default function BookUploadForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const asyncUpload = useAsyncUploadBook();

  // Fetch active publishers for dropdown
  const {
    data: publishers = [],
    isLoading: isLoadingPublishers,
    error: publishersError,
  } = useQuery({
    queryKey: ['publishers', 'active'],
    queryFn: getActivePublishers,
  });

  const [formData, setFormData] = useState<BookUploadDto>({
    title: '',
    author: '',
    isbn: '',
    publisherId: '',
    format: BookFormat.Epub,
    defaultPrintQuotaLimit: null,
    defaultPrintQuotaWindowDays: null,
    defaultCopyQuotaLimit: null,
    defaultCopyQuotaWindowDays: null,
    defaultDownloadQuotaLimit: null,
    defaultDownloadQuotaWindowDays: null,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isbnError, setIsbnError] = useState<string | null>(null);
  const [showDrmQuotas, setShowDrmQuotas] = useState(false);

  // Navigate to book reader when processing completes successfully
  useEffect(() => {
    if (asyncUpload.isComplete && asyncUpload.bookId) {
      navigate(`/read/${asyncUpload.bookId}`);
    }
  }, [asyncUpload.isComplete, asyncUpload.bookId, navigate]);

  // Validate file
  const validateFile = (file: File): string | null => {
    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_FORMATS.includes(fileExtension)) {
      return t('App.Upload.BookUploadForm.InvalidFormat', { formats: ALLOWED_FORMATS.join(', ') });
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const maxSizeMB = Math.floor(MAX_FILE_SIZE / (1024 * 1024));
      const fileSizeMB = Math.floor(file.size / (1024 * 1024));
      return t('App.Upload.BookUploadForm.FileTooLarge', { fileSizeMB, maxSizeMB });
    }

    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      setSelectedFile(null);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);

    // Auto-populate title from filename if not already set
    if (!formData.title) {
      const filename = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setFormData(prev => ({ ...prev, title: filename }));
    }
  }, [formData.title]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle numeric input changes (for DRM quotas)
  const handleNumericInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Convert to number or null if empty
    const numericValue = value === '' ? null : parseInt(value, 10);
    setFormData(prev => ({ ...prev, [name]: numericValue }));
  };

  // Handle ISBN input with validation
  const handleISBNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, isbn: value }));

    // Clear error if field is empty
    if (!value.trim()) {
      setIsbnError(null);
      return;
    }

    // Validate ISBN on change
    const result = validateISBN(value);
    if (!result.isValid) {
      setIsbnError(result.error || t('App.Upload.BookUploadForm.InvalidIsbnDefault'));
    } else {
      setIsbnError(null);
      // Update with formatted ISBN
      setFormData(prev => ({ ...prev, isbn: result.formattedISBN }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      setValidationError(t('App.Upload.BookUploadForm.SelectFileRequired'));
      return;
    }

    if (!formData.title.trim()) {
      setValidationError(t('App.Upload.BookUploadForm.TitleRequired'));
      return;
    }

    if (!formData.author.trim()) {
      setValidationError(t('App.Upload.BookUploadForm.AuthorRequired'));
      return;
    }

    // Validate ISBN if provided
    if (formData.isbn.trim()) {
      const isbnResult = validateISBN(formData.isbn);
      if (!isbnResult.isValid) {
        setValidationError(isbnResult.error || t('App.Upload.BookUploadForm.InvalidISBN'));
        setIsbnError(isbnResult.error || t('App.Upload.BookUploadForm.InvalidISBN'));
        return;
      }
    }

    // Check if publisher is required (you may want to make this optional)
    if (!formData.publisherId.trim()) {
      setValidationError(t('App.Upload.BookUploadForm.PublisherRequired'));
      return;
    }

    try {
      setValidationError(null);

      // Start async upload (file upload + background processing)
      asyncUpload.upload({
        data: formData,
        file: selectedFile,
        coverImage: selectedCoverImage ?? undefined,
      });

      // Note: Navigation happens automatically via useEffect when processing completes
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message
        || error.message
        || t('App.Upload.BookUploadForm.UploadFailed');
      setValidationError(errorMessage);
    }
  };

  // Handle cover image selection
  const handleCoverImageSelect = useCallback((file: File) => {
    // Validate image file
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedImageTypes.includes(file.type)) {
      setValidationError(t('App.Upload.BookUploadForm.InvalidCoverFormat'));
      return;
    }

    // Check file size (max 5MB for cover images)
    const maxImageSize = 5 * 1024 * 1024;
    if (file.size > maxImageSize) {
      setValidationError(t('App.Upload.BookUploadForm.CoverTooLarge'));
      return;
    }

    setSelectedCoverImage(file);
  }, []);

  // Handle cover image input change
  const handleCoverImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleCoverImageSelect(files[0]);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      title: '',
      author: '',
      isbn: '',
      publisherId: '',
      format: BookFormat.Epub,
      defaultPrintQuotaLimit: null,
      defaultPrintQuotaWindowDays: null,
      defaultCopyQuotaLimit: null,
      defaultCopyQuotaWindowDays: null,
      defaultDownloadQuotaLimit: null,
      defaultDownloadQuotaWindowDays: null,
    });
    setSelectedFile(null);
    setSelectedCoverImage(null);
    setValidationError(null);
    setShowDrmQuotas(false);
    asyncUpload.reset();
  };

  const isUploading = asyncUpload.isUploading;
  const isProcessing = asyncUpload.isProcessing;
  const isBusy = isUploading || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="card max-w-2xl mx-auto">
      <h2 className="heading-2 mb-6">{t('App.Upload.BookUploadForm.Title')}</h2>

      {validationError && (
        <ErrorMessage
          message={validationError}
          onDismiss={() => setValidationError(null)}
          className="mb-6"
        />
      )}

      {/* File Upload Area */}
      <div className="mb-6">
        <label className="label">{t('App.Upload.BookUploadForm.FileLabel')}</label>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors
            ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-neutral-300'}
            ${selectedFile ? 'bg-success-50 border-success-500' : ''}
          `}
        >
          <input
            type="file"
            accept=".epub,.pdf"
            onChange={handleFileInputChange}
            disabled={isBusy}
            className="hidden"
            id="file-upload"
          />

          {!selectedFile ? (
            <>
              <svg
                className="mx-auto h-12 w-12 text-neutral-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="mt-2 text-sm text-neutral-600">
                {t('App.Upload.BookUploadForm.DragDropHint')}
              </p>
              <label
                htmlFor="file-upload"
                className="mt-2 inline-block cursor-pointer text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('App.Upload.BookUploadForm.BrowseLink')}
              </label>
              <p className="mt-2 text-xs text-neutral-500">
                {t('App.Upload.BookUploadForm.MaxSizeHint')}
              </p>
            </>
          ) : (
            <>
              <svg
                className="mx-auto h-12 w-12 text-success-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="mt-2 text-sm font-medium text-neutral-900">
                {selectedFile.name}
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>
              {!isUploading && (
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="mt-2 text-sm text-error-600 hover:text-error-700"
                >
                  {t('App.Upload.BookUploadForm.RemoveFile')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upload Progress & Processing Status */}
      {(isUploading || isProcessing) && (
        <div className="mb-6">
          {/* File Upload Progress */}
          {isUploading && (
            <>
              <div className="flex justify-between text-sm text-neutral-600 mb-2">
                <span>{t('App.Upload.BookUploadForm.UploadingLabel')}</span>
                <span>{asyncUpload.uploadProgress}%</span>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-2 mb-4">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${asyncUpload.uploadProgress}%` }}
                />
              </div>
            </>
          )}

          {/* Processing Status */}
          {isProcessing && asyncUpload.status && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <LoadingSpinner size="sm" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary-900">
                    {asyncUpload.status.status === 'Queued' && t('App.Upload.BookUploadForm.ProcessingQueued')}
                    {asyncUpload.status.status === 'Processing' && t('App.Upload.BookUploadForm.Processing')}
                  </p>
                  <p className="text-xs text-primary-700 mt-1">
                    {asyncUpload.status.fileName && t('App.Upload.BookUploadForm.ProcessingFile', { fileName: asyncUpload.status.fileName })}
                  </p>
                  {asyncUpload.status.status === 'Processing' && (
                    <p className="text-xs text-primary-600 mt-1">
                      {t('App.Upload.BookUploadForm.ProcessingWait')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {asyncUpload.isFailed && asyncUpload.error && (
            <ErrorMessage
              message={asyncUpload.error.message || t('App.Upload.BookUploadForm.UploadFailed')}
              onDismiss={handleReset}
              className="mt-4"
            />
          )}
        </div>
      )}

      <div className="divider" />

      {/* Book Metadata */}
      <div className="space-y-4">
        <div>
          <label htmlFor="title" className="label">
            {t('App.Upload.BookUploadForm.TitleLabel')}
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            disabled={isBusy}
            className="input"
            placeholder={t('App.Upload.BookUploadForm.TitlePlaceholder')}
            required
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="author" className="label">
            {t('App.Upload.BookUploadForm.AuthorLabel')}
          </label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleInputChange}
            disabled={isBusy}
            className="input"
            placeholder={t('App.Upload.BookUploadForm.AuthorPlaceholder')}
            required
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="isbn" className="label">
            {t('App.Upload.BookUploadForm.IsbnLabel')}
          </label>
          <input
            type="text"
            id="isbn"
            name="isbn"
            value={formData.isbn}
            onChange={handleISBNChange}
            disabled={isBusy}
            className={`input ${isbnError ? 'border-error-500 focus:ring-error-500' : ''}`}
            placeholder={t('App.Upload.BookUploadForm.IsbnPlaceholder')}
            maxLength={20}
            autoComplete="off"
            aria-invalid={!!isbnError}
            aria-describedby={isbnError ? "isbn-error" : undefined}
          />
          {isbnError && (
            <p id="isbn-error" role="alert" className="mt-1 text-sm text-error-600">{isbnError}</p>
          )}
          <p className="mt-1 text-xs text-neutral-500">
            {t('App.Upload.BookUploadForm.IsbnHint')}
          </p>
        </div>

        <div>
          <label htmlFor="publisherId" className="label">
            {t('App.Upload.BookUploadForm.PublisherLabel')}
          </label>
          {isLoadingPublishers ? (
            <div className="flex items-center gap-2 p-3 border border-neutral-300 rounded-md bg-neutral-50">
              <LoadingSpinner size="sm" />
              <span className="text-sm text-neutral-600">{t('App.Upload.BookUploadForm.PublishersLoading')}</span>
            </div>
          ) : publishersError ? (
            <div className="p-3 border border-error-300 rounded-md bg-error-50 text-error-700 text-sm">
              {t('App.Upload.BookUploadForm.PublishersLoadError')}
            </div>
          ) : (
            <>
              <select
                id="publisherId"
                name="publisherId"
                value={formData.publisherId}
                onChange={handleInputChange}
                disabled={isBusy}
                className="input"
                required
              >
                <option value="">{t('App.Upload.BookUploadForm.PublisherDefault')}</option>
                {publishers.map((publisher: PublisherDto) => (
                  <option key={publisher.id} value={publisher.id}>
                    {publisher.name}
                  </option>
                ))}
              </select>
              {publishers.length === 0 && (
                <p className="mt-1 text-sm text-warning-600">
                  {t('App.Upload.BookUploadForm.NoPublishers')}
                </p>
              )}
            </>
          )}
        </div>

        {/* DRM Quota Settings (Optional) */}
        <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
          <button
            type="button"
            onClick={() => setShowDrmQuotas(!showDrmQuotas)}
            className="w-full flex items-center justify-between text-left"
            disabled={isBusy}
          >
            <div>
              <h3 className="font-medium text-neutral-900">{t('App.Upload.BookUploadForm.DrmQuotasTitle')}</h3>
              <p className="mt-1 text-sm text-neutral-500">
                {t('App.Upload.BookUploadForm.DrmQuotasHint')}
              </p>
            </div>
            <svg
              className={`h-5 w-5 text-neutral-400 transition-transform ${showDrmQuotas ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDrmQuotas && (
            <div className="mt-4 space-y-4">
              {/* Print Quota */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="defaultPrintQuotaLimit" className="label text-sm">
                    {t('App.Upload.BookUploadForm.PrintQuotaLimitLabel')}
                  </label>
                  <input
                    type="number"
                    id="defaultPrintQuotaLimit"
                    name="defaultPrintQuotaLimit"
                    value={formData.defaultPrintQuotaLimit ?? ''}
                    onChange={handleNumericInputChange}
                    disabled={isBusy}
                    className="input"
                    placeholder={t('App.Upload.BookUploadForm.PrintQuotaLimitPlaceholder')}
                    min="0"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('App.Upload.BookUploadForm.PrintQuotaLimitHint')}
                  </p>
                </div>
                <div>
                  <label htmlFor="defaultPrintQuotaWindowDays" className="label text-sm">
                    {t('App.Upload.BookUploadForm.PrintQuotaWindowLabel')}
                  </label>
                  <input
                    type="number"
                    id="defaultPrintQuotaWindowDays"
                    name="defaultPrintQuotaWindowDays"
                    value={formData.defaultPrintQuotaWindowDays ?? ''}
                    onChange={handleNumericInputChange}
                    disabled={isBusy}
                    className="input"
                    placeholder={t('App.Upload.BookUploadForm.PrintQuotaWindowPlaceholder')}
                    min="1"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('App.Upload.BookUploadForm.PrintQuotaWindowHint')}
                  </p>
                </div>
              </div>

              {/* Copy Quota */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="defaultCopyQuotaLimit" className="label text-sm">
                    {t('App.Upload.BookUploadForm.CopyQuotaLimitLabel')}
                  </label>
                  <input
                    type="number"
                    id="defaultCopyQuotaLimit"
                    name="defaultCopyQuotaLimit"
                    value={formData.defaultCopyQuotaLimit ?? ''}
                    onChange={handleNumericInputChange}
                    disabled={isBusy}
                    className="input"
                    placeholder={t('App.Upload.BookUploadForm.CopyQuotaLimitPlaceholder')}
                    min="0"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('App.Upload.BookUploadForm.CopyQuotaLimitHint')}
                  </p>
                </div>
                <div>
                  <label htmlFor="defaultCopyQuotaWindowDays" className="label text-sm">
                    {t('App.Upload.BookUploadForm.CopyQuotaWindowLabel')}
                  </label>
                  <input
                    type="number"
                    id="defaultCopyQuotaWindowDays"
                    name="defaultCopyQuotaWindowDays"
                    value={formData.defaultCopyQuotaWindowDays ?? ''}
                    onChange={handleNumericInputChange}
                    disabled={isBusy}
                    className="input"
                    placeholder={t('App.Upload.BookUploadForm.CopyQuotaWindowPlaceholder')}
                    min="1"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('App.Upload.BookUploadForm.CopyQuotaWindowHint')}
                  </p>
                </div>
              </div>

              {/* Download Quota */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="defaultDownloadQuotaLimit" className="label text-sm">
                    {t('App.Upload.BookUploadForm.DownloadQuotaLimitLabel')}
                  </label>
                  <input
                    type="number"
                    id="defaultDownloadQuotaLimit"
                    name="defaultDownloadQuotaLimit"
                    value={formData.defaultDownloadQuotaLimit ?? ''}
                    onChange={handleNumericInputChange}
                    disabled={isBusy}
                    className="input"
                    placeholder={t('App.Upload.BookUploadForm.DownloadQuotaLimitPlaceholder')}
                    min="0"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('App.Upload.BookUploadForm.DownloadQuotaLimitHint')}
                  </p>
                </div>
                <div>
                  <label htmlFor="defaultDownloadQuotaWindowDays" className="label text-sm">
                    {t('App.Upload.BookUploadForm.DownloadQuotaWindowLabel')}
                  </label>
                  <input
                    type="number"
                    id="defaultDownloadQuotaWindowDays"
                    name="defaultDownloadQuotaWindowDays"
                    value={formData.defaultDownloadQuotaWindowDays ?? ''}
                    onChange={handleNumericInputChange}
                    disabled={isBusy}
                    className="input"
                    placeholder={t('App.Upload.BookUploadForm.DownloadQuotaWindowPlaceholder')}
                    min="1"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('App.Upload.BookUploadForm.DownloadQuotaWindowHint')}
                  </p>
                </div>
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-md p-3 text-sm text-primary-800">
                <p className="font-medium">ℹ️ {t('App.Upload.BookUploadForm.DrmInfoTitle')}</p>
                <ul className="mt-1 space-y-1 text-xs">
                  <li>• {t('App.Upload.BookUploadForm.DrmInfoItem1')}</li>
                  <li>• {t('App.Upload.BookUploadForm.DrmInfoItem2')}</li>
                  <li>• {t('App.Upload.BookUploadForm.DrmInfoItem3')}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Cover Image Upload (Optional) */}
        <div>
          <label className="label">{t('App.Upload.BookUploadForm.CoverImageLabel')}</label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleCoverImageInputChange}
              disabled={isBusy}
              className="hidden"
              id="cover-image-upload"
            />
            <label
              htmlFor="cover-image-upload"
              className="btn btn-secondary cursor-pointer"
            >
              {selectedCoverImage ? t('App.Upload.BookUploadForm.ChangeCoverImage') : t('App.Upload.BookUploadForm.ChooseCoverImage')}
            </label>
            {selectedCoverImage && (
              <div className="flex items-center gap-2">
                <div className="text-sm text-neutral-700">
                  {selectedCoverImage.name} ({(selectedCoverImage.size / 1024).toFixed(1)} KB)
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedCoverImage(null)}
                  disabled={isBusy}
                  className="text-sm text-error-600 hover:text-error-700"
                >
                  {t('App.Upload.BookUploadForm.RemoveCoverImage')}
                </button>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            {t('App.Upload.BookUploadForm.CoverImageHint')}
          </p>
        </div>
      </div>

      <div className="divider" />

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end">
        <button
          type="button"
          onClick={handleReset}
          disabled={isBusy}
          className="btn btn-secondary"
        >
          {t('App.Upload.BookUploadForm.ResetButton')}
        </button>
        <button
          type="submit"
          disabled={isBusy || !selectedFile}
          className="btn btn-primary"
        >
          {isUploading ? (
            <>
              <span
                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                role="status"
                aria-label="Loading"
              />
              <span className="ml-2">{t('App.Upload.BookUploadForm.UploadingButton')}</span>
            </>
          ) : isProcessing ? (
            <>
              <span
                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                role="status"
                aria-label="Loading"
              />
              <span className="ml-2">{t('App.Upload.BookUploadForm.ProcessingButton')}</span>
            </>
          ) : (
            t('App.Upload.BookUploadForm.UploadButton')
          )}
        </button>
      </div>
    </form>
  );
}
